import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import useGetDRMEntityInfo from 'v2/hooks/useGetDRMEntityInfo';
import { LinkEntityType } from 'v2/models';
import ConfirmationModal, {
  ConfirmationModalProps,
} from '../ConfirmationModal/ConfirmationModal';
import styles from './linkEntity.module.scss';
import { FormControl, Spinner } from 'react-bootstrap';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import EntityBox from './EntityBox';
import AccordionEntityContent from './AccordionEntityContent';
import { validateLink } from 'v2/utils/linkEntityutils';
import classnames from 'classnames';
import { useAddSearchDRMLinkActivity } from './useAddSearchDRMLinkActivity';
import { toast } from 'react-toastify';
import commentDotsIcon from 'v2/icons/commentDots.svg';

interface LinkEntityModalProps {
  show: boolean;
  onClose?: () => void;
  searchId: number;
}

export interface LinkEntityFormFields {
  currentEntity?: LinkEntityType;
  link: string;
}

const LinkEntityModal = ({ show, onClose, searchId }: LinkEntityModalProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    reset: resetForm,
    formState: { isValid, isDirty },
    watch,
  } = useForm<LinkEntityFormFields>({ mode: 'onChange' });

  const currentEntity = watch('currentEntity');

  useEffect(() => {
    if (!currentEntity) {
      setFocus('link');
    }
  }, [currentEntity]);

  const {
    data: drmEntity,
    isSuccess: isdrmEntityLoadSuccess,
    isFetching,
  } = useGetDRMEntityInfo(currentEntity);

  const isValidForm = isValid && isdrmEntityLoadSuccess;

  const closeModal = () => {
    clearForm();
    onClose?.();
  };

  const clearForm = () => {
    resetForm({ currentEntity: undefined, link: '' });
  };

  const {
    mutateAsync: createLink,
    reset,
    isSuccess,
    isLoading,
  } = useAddSearchDRMLinkActivity(searchId);

  const onSubmit: SubmitHandler<LinkEntityFormFields> = async (data) => {
    if (!data.currentEntity) {
      toast.error('Failed to create link');
      return;
    }

    try {
      await createLink(data as Required<LinkEntityFormFields>);
      toast.success('Link created');
    } catch (error) {
      toast.error('Could not create link');
    }
  };

  const handleClickCancel: ConfirmationModalProps['onCancel'] = (source) => {
    if (isSuccess && source === 'cancel') {
      reset();
      clearForm();
    } else {
      closeModal();
    }
  };

  const handleOK = () => {
    if (isSuccess) {
      closeModal();
    } else {
      handleSubmit(onSubmit)();
    }
  };

  const okText = isSuccess ? 'Done' : 'Save';
  const cancelText = isSuccess ? 'Link Another Entity' : 'Cancel';

  return (
    <ConfirmationModal
      show={show}
      title={`Link an Entity`}
      onCancel={handleClickCancel}
      onOk={handleOK}
      isLoading={isLoading}
      okBtnDisabled={!isValidForm}
      okBtnText={okText}
      className={styles.viewModals}
      centered={true}
      animation={true}
      cancelBtnDisabled={isLoading}
      cancelBtnText={cancelText}
    >
      {isSuccess ? (
        <div className={classnames(styles.success)}>
          <h1>Success!</h1>
          <p>Your entity has been linked to this Request.</p>
          <div className={classnames('comment')}>
            <img alt="activity conversation icon" src={commentDotsIcon}></img>
            <p>
              A conversation has been created with the originator and this
              entity has been shared. Click on the Conversations tab to give the
              user more context.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <FormLabel label={`Entity`} required={false} />
          {currentEntity && drmEntity ? (
            <EntityBox entity={drmEntity} onRemove={clearForm} />
          ) : (
            <>
              <div>
                <FormControl
                  className={styles.inputText}
                  type="text"
                  autoComplete="off"
                  placeholder="Paste entity location"
                  aria-label="link_input"
                  isInvalid={!isValidForm && isDirty}
                  {...register('link', {
                    required: true,
                    validate: (link) => {
                      const [isValid, entity] = validateLink(link);
                      setValue('currentEntity', entity);
                      return isValid;
                    },
                  })}
                />

                {isFetching ? (
                  <div className={styles.loading}>
                    <Spinner
                      className={classnames(styles.spinner)}
                      animation="border"
                    />
                  </div>
                ) : (
                  <FormControl.Feedback type="invalid">
                    The entered value is invalid.
                  </FormControl.Feedback>
                )}
              </div>
            </>
          )}
          <FormControl
            type="hidden"
            readOnly={true}
            aria-label="entity-hidden-input"
            {...register('currentEntity', {
              required: true,
            })}
          />
          <AccordionEntityContent />
        </div>
      )}
    </ConfirmationModal>
  );
};

export default LinkEntityModal;
